import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Upload, X } from "lucide-react";
import api, { eur, apiErr } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  name: "", greek_name: "", description: "", materials: "", dtf_info: "", category: "",
  price: 19.9, sale_price: null, badge: "", is_featured: false, is_best_seller: false,
  is_new: false, is_limited: false, active: true, sizes: [], colors: [], variants: [],
  images: [], image_front: "", image_back: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [colorInput, setColorInput] = useState("");
  const [sizeInput, setSizeInput] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({ ...EMPTY, ...p, sale_price: p.sale_price ?? null, badge: p.badge || "" });
    setEditId(p.id); setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const rebuildVariants = (colors, sizes) => {
    const existing = {};
    form.variants.forEach((v) => { existing[`${v.color}|${v.size}`] = v.stock; });
    const variants = [];
    colors.forEach((c) => sizes.forEach((s) => variants.push({ color: c, size: s, stock: existing[`${c}|${s}`] ?? 0 })));
    return variants;
  };
  const addColor = () => { if (!colorInput.trim()) return; const colors = [...form.colors, colorInput.trim()]; setForm((f) => ({ ...f, colors, variants: rebuildVariants(colors, f.sizes) })); setColorInput(""); };
  const removeColor = (c) => { const colors = form.colors.filter((x) => x !== c); setForm((f) => ({ ...f, colors, variants: rebuildVariants(colors, f.sizes) })); };
  const addSize = () => { if (!sizeInput.trim()) return; const sizes = [...form.sizes, sizeInput.trim()]; setForm((f) => ({ ...f, sizes, variants: rebuildVariants(f.colors, sizes) })); setSizeInput(""); };
  const removeSize = (s) => { const sizes = form.sizes.filter((x) => x !== s); setForm((f) => ({ ...f, sizes, variants: rebuildVariants(f.colors, sizes) })); };
  const setVariantStock = (c, s, stock) => setForm((f) => ({ ...f, variants: f.variants.map((v) => v.color === c && v.size === s ? { ...v, stock: parseInt(stock) || 0 } : v) }));

  const uploadImg = async (e, field) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imgs = [...form.images];
      if (field === "front") { set("image_front", data.url); imgs[0] = data.url; }
      else { set("image_back", data.url); imgs[1] = data.url; }
      set("images", imgs.filter(Boolean));
      toast.success("Η εικόνα ανέβηκε");
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name || !form.category) { toast.error("Συμπλήρωσε όνομα και κατηγορία"); return; }
    const payload = { ...form, price: parseFloat(form.price), sale_price: form.sale_price ? parseFloat(form.sale_price) : null, badge: form.badge || null, images: [form.image_front, form.image_back].filter(Boolean) };
    try {
      if (editId) await api.put(`/admin/products/${editId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editId ? "Το προϊόν ενημερώθηκε" : "Το προϊόν δημιουργήθηκε");
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Διαγραφή προϊόντος;")) return; await api.delete(`/admin/products/${id}`); toast.success("Διαγράφηκε"); load(); };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const inp = "w-full bg-[#0A0A0B] border border-zinc-700 rounded px-3 py-2.5 text-sm outline-none focus:border-[#FF2E4C]";

  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Products" subtitle="ΚΑΤΑΛΟΓΟΣ" action={
        <button onClick={openNew} className="btn-red px-5 py-2.5 text-xs font-bold tracking-widest uppercase rounded flex items-center gap-2" data-testid="admin-add-product-button"><Plus className="w-4 h-4" /> ΝΕΟ ΠΡΟΪΟΝ</button>
      } />

      <div className="flex items-center gap-2 bg-[#18181C] border border-zinc-700 rounded px-3 mb-6 max-w-md">
        <Search className="w-4 h-4 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση..." className="flex-1 bg-transparent py-2.5 text-sm outline-none" data-testid="admin-product-search" />
      </div>

      <div className="card-surface rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-zinc-500 font-mono text-xs uppercase border-b border-zinc-800">
            <th className="p-4">Προϊόν</th><th className="p-4">Κατηγορία</th><th className="p-4">Τιμή</th><th className="p-4">Απόθεμα</th><th className="p-4">Status</th><th className="p-4"></th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-800">
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-white/[0.02]" data-testid={`admin-product-row-${p.slug}`}>
                <td className="p-4"><div className="flex items-center gap-3"><img src={p.image_front} alt="" className="w-10 h-12 object-cover rounded" /><span className="font-semibold">{p.name}</span></div></td>
                <td className="p-4 text-zinc-400">{p.category}</td>
                <td className="p-4">{p.sale_price ? <span><span className="text-[#FF2E4C]">{eur(p.sale_price)}</span> <span className="text-zinc-600 line-through text-xs">{eur(p.price)}</span></span> : eur(p.price)}</td>
                <td className="p-4"><span className={`font-mono ${p.total_stock === 0 ? "text-red-500" : p.total_stock <= 10 ? "text-yellow-500" : "text-zinc-300"}`}>{p.total_stock}</span></td>
                <td className="p-4">{p.active ? <span className="text-green-500 text-xs">● Active</span> : <span className="text-zinc-600 text-xs">○ Hidden</span>}</td>
                <td className="p-4"><div className="flex gap-2 justify-end">
                  <button onClick={() => openEdit(p)} className="p-2 hover:text-[#FF2E4C]" data-testid={`admin-edit-product-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} className="p-2 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121215] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Επεξεργασία" : "Νέο"} Προϊόν</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-mono uppercase text-zinc-500">Όνομα *</label><input value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} data-testid="pf-name" /></div>
              <div><label className="text-xs font-mono uppercase text-zinc-500">Κατηγορία *</label>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inp} data-testid="pf-category">
                  <option value="">Επιλογή...</option>
                  {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div><label className="text-xs font-mono uppercase text-zinc-500">Τιμή (€) *</label><input type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="pf-price" /></div>
              <div><label className="text-xs font-mono uppercase text-zinc-500">Τιμή Προσφοράς (€)</label><input type="number" step="0.01" value={form.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value || null)} className={inp} data-testid="pf-sale" /></div>
              <div className="col-span-2"><label className="text-xs font-mono uppercase text-zinc-500">Badge</label>
                <select value={form.badge} onChange={(e) => set("badge", e.target.value)} className={inp}>
                  <option value="">Κανένα</option><option>NEW DROP</option><option>BEST SELLER</option><option>LIMITED EDITION</option><option>SALE</option>
                </select>
              </div>
            </div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Περιγραφή</label><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className={inp} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-mono uppercase text-zinc-500">Υλικά</label><textarea value={form.materials} onChange={(e) => set("materials", e.target.value)} rows={2} className={inp} /></div>
              <div><label className="text-xs font-mono uppercase text-zinc-500">DTF Info</label><textarea value={form.dtf_info} onChange={(e) => set("dtf_info", e.target.value)} rows={2} className={inp} /></div>
            </div>

            {/* IMAGES */}
            <div className="grid grid-cols-2 gap-3">
              {["front", "back"].map((f) => {
                const url = f === "front" ? form.image_front : form.image_back;
                return (
                  <div key={f}>
                    <label className="text-xs font-mono uppercase text-zinc-500">Εικόνα {f === "front" ? "Μπροστά" : "Πίσω"}</label>
                    <div className="mt-1 border border-dashed border-zinc-700 rounded p-3 text-center">
                      {url && <img src={url} alt="" className="w-full h-32 object-cover rounded mb-2" />}
                      <label className="cursor-pointer inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-[#FF2E4C]">
                        <Upload className="w-4 h-4" /> {uploading ? "..." : "Ανέβασμα"}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e, f)} data-testid={`pf-upload-${f}`} />
                      </label>
                      <input value={url || ""} onChange={(e) => { set(f === "front" ? "image_front" : "image_back", e.target.value); }} placeholder="ή URL" className={`${inp} mt-2 text-xs`} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* COLORS & SIZES */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-mono uppercase text-zinc-500">Χρώματα</label>
                <div className="flex gap-2 mt-1"><input value={colorInput} onChange={(e) => setColorInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addColor())} className={inp} placeholder="π.χ. Black" data-testid="pf-color-input" /><button onClick={addColor} className="px-3 bg-white text-black rounded text-xs font-bold">+</button></div>
                <div className="flex flex-wrap gap-1.5 mt-2">{form.colors.map((c) => <span key={c} className="flex items-center gap-1 bg-zinc-800 rounded px-2 py-1 text-xs">{c}<button onClick={() => removeColor(c)}><X className="w-3 h-3" /></button></span>)}</div>
              </div>
              <div>
                <label className="text-xs font-mono uppercase text-zinc-500">Μεγέθη</label>
                <div className="flex gap-2 mt-1"><input value={sizeInput} onChange={(e) => setSizeInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSize())} className={inp} placeholder="π.χ. M" data-testid="pf-size-input" /><button onClick={addSize} className="px-3 bg-white text-black rounded text-xs font-bold">+</button></div>
                <div className="flex flex-wrap gap-1.5 mt-2">{form.sizes.map((s) => <span key={s} className="flex items-center gap-1 bg-zinc-800 rounded px-2 py-1 text-xs">{s}<button onClick={() => removeSize(s)}><X className="w-3 h-3" /></button></span>)}</div>
              </div>
            </div>

            {/* VARIANT STOCK MATRIX */}
            {form.variants.length > 0 && (
              <div>
                <label className="text-xs font-mono uppercase text-zinc-500">Απόθεμα ανά Παραλλαγή</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {form.variants.map((v) => (
                    <div key={`${v.color}|${v.size}`} className="flex items-center gap-2 bg-[#0A0A0B] rounded p-2">
                      <span className="text-xs flex-1 truncate">{v.color}/{v.size}</span>
                      <input type="number" value={v.stock} onChange={(e) => setVariantStock(v.color, v.size, e.target.value)} className="w-14 bg-transparent border border-zinc-700 rounded px-1.5 py-1 text-xs text-center" data-testid={`pf-stock-${v.color}-${v.size}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FLAGS */}
            <div className="flex flex-wrap gap-4">
              {[["is_new", "New"], ["is_best_seller", "Best Seller"], ["is_featured", "Featured"], ["is_limited", "Limited"], ["active", "Active"]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`pf-flag-${k}`} /> {l}</label>
              ))}
            </div>

            <button onClick={save} className="btn-red w-full py-3 text-sm font-bold tracking-widest uppercase rounded" data-testid="pf-save">ΑΠΟΘΗΚΕΥΣΗ</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
